
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Search, BarChart3, User, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

const MobileNavigation = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const isHomePage = location.pathname === '/';

  // Improved active state detection
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { name: t("nav.home"), path: "/", icon: Home },
    { name: t("nav.properties"), path: "/properties", icon: Search },
    { name: t("nav.proptalk"), path: "/proptalk", icon: MessageSquare },
    { name: t("nav.investments"), path: "/investments", icon: BarChart3 },
    { name: t("nav.account"), path: "/account", icon: User }
  ];

  return (
    <>
      {/* Bottom navigation for mobile only - Fixed positioning with enterprise styling */}
      <nav 
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-propmatch-dark/98 backdrop-blur-xl border-t border-white/10 shadow-2xl shadow-black/20"
        style={{ 
          paddingBottom: 'max(env(safe-area-inset-bottom), 0.5rem)',
          height: 'calc(4rem + env(safe-area-inset-bottom))'
        }}
        role="navigation"
        aria-label="Mobile navigation"
      >
        <div className="grid grid-cols-5 h-16 mx-auto max-w-md px-2">
          {navItems.map((item, index) => (
            <Link 
              key={item.name} 
              to={item.path} 
              className={cn(
                "flex flex-col items-center justify-center h-full transition-all duration-300 ease-out relative touch-manipulation rounded-lg mx-1", 
                isActive(item.path) 
                  ? "text-propmatch-blue-light bg-white/10 transform scale-105 shadow-lg" 
                  : "text-white/70 hover:bg-white/5 hover:text-white active:scale-95"
              )}
              aria-label={item.name}
              role="tab"
              aria-selected={isActive(item.path)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Active indicator */}
              {isActive(item.path) && (
                <div className="absolute top-1 left-1/2 transform -translate-x-1/2 w-6 h-1 bg-propmatch-blue-light rounded-full animate-scale-in" />
              )}
              
              <item.icon 
                size={20} 
                className={cn(
                  "mb-1 transition-all duration-200", 
                  isActive(item.path) ? "text-propmatch-blue-light drop-shadow-sm" : "text-white/70"
                )} 
              />
              <span className={cn(
                "text-[9px] font-medium transition-all duration-200 text-center",
                isActive(item.path) ? "text-propmatch-blue-light" : "text-white/70"
              )}>
                {item.name}
              </span>
              
              {/* Ripple effect on tap */}
              <div className="absolute inset-0 rounded-lg overflow-hidden">
                <div className="absolute inset-0 bg-white/10 scale-0 rounded-lg transition-transform duration-300 group-active:scale-150" />
              </div>
            </Link>
          ))}
        </div>
      </nav>

      {/* Language switcher for mobile - only show on non-home pages */}
      {!isHomePage && (
        <LanguageSwitcher 
          className="fixed bottom-28 left-4 z-40 animate-fade-in" 
          size="sm" 
          variant="glass"
          style={{ bottom: 'calc(7rem + env(safe-area-inset-bottom))' }}
        />
      )}
    </>
  );
};

export default MobileNavigation;
